import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { ReactElement, Ref, TextareaHTMLAttributes } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { useComposedRefs } from '../../utils/compose-refs';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
    readonly autoResize?: boolean;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly maxRows?: number;
    readonly placeholder?: string;
    readonly ref?: Ref<HTMLTextAreaElement>;
    readonly rows?: number;
    readonly variant?: 'default' | 'error' | 'success' | 'ghost';
};

const variantClasses = {
    default: 'border-border-muted',
    error: 'border-border-error',
    success: 'border-border-success',
    ghost: '',
} as const;

const variantFocusClasses = {
    default:
        'hover:border-border-control-focus hover:surface-text-control-recessed focus:border-border-control-focus focus:surface-text-control-recessed',
    error: 'hover:border-border-error-strong focus:border-border-error-strong',
    success: 'hover:border-border-success-strong focus:border-border-success-strong',
    ghost: '',
} as const;

export const Textarea = ({
    autoResize = false,
    className = '',
    disabled = false,
    maxRows,
    placeholder,
    ref,
    rows = 3,
    variant = 'default',
    ...props
}: TextareaProps): ReactElement => {
    const { onInput, style, value, ...restProps } = props;
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const adjustHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (is.nullOrUndefined(textarea)) {
            return;
        }

        textarea.style.height = 'auto';

        if (is.undefined(maxRows)) {
            textarea.style.height = `${textarea.scrollHeight}px`;
            return;
        }

        const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight) || 20;
        const maxHeight = maxRows * lineHeight;
        const nextHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [maxRows]);

    const composedRef = useComposedRefs(ref, textareaRef);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        if (autoResize) {
            adjustHeight();
        }
    }, [autoResize, value, adjustHeight]);

    const isGhost = variant === 'ghost';

    return (
        <textarea
            {...restProps}
            ref={composedRef}
            className={clsx(
                'w-full box-border outline-none resize-none font-sans',
                isGhost
                    ? 'border-none bg-transparent p-0'
                    : [
                          'bg-surface-raised text-text-form rounded-lg',
                          'border px-l py-m',
                          'text-meta font-normal tracking-tight',
                          'placeholder:text-text-content-subtle',
                          'transition-[border-color,box-shadow,background-color,color,opacity] duration-100 ease-in-out',
                          variantClasses[variant],
                      ],
                disabled ? 'cursor-default opacity-50' : clsx('cursor-text', variantFocusClasses[variant]),
                className,
            )}
            disabled={disabled}
            placeholder={placeholder}
            rows={rows}
            style={autoResize ? { ...style, overflow: maxRows ? undefined : 'hidden' } : style}
            value={value}
            onInput={(event) => {
                onInput?.(event);
                if (autoResize) adjustHeight();
            }}
        />
    );
};
